import { Table as ChakraTable } from '@chakra-ui/react';
import { InfiniteData } from '@tanstack/query-core';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import { ListQueryRequestType, QueryOptionsT } from 'shared/api/types/typedQueries';
import { FilterField } from 'shared/types/filters';
import { InfiniteQueryResult } from '../../api/types/typedQueries';
import useGetSortingMethods from './hooks/useGetSortingMethods';
import TableBody from './TableBody';
import TableFooter from './TableFooter';
import TableHead from './TableHead';
import { useState } from 'react';
import { TableNameE } from 'shared/api/types/common';
import TableContainer from './TableContainer';
import { DefaultTableData } from './DraggableRow';

interface Props<QueryData extends ListQueryRequestType<DefaultTableData>> {
  columns: ColumnDef<ArrayElement<QueryData['response']['data']>>[];
  query: (
    params?: Pick<QueryData, 'pathParams' | 'params'>,
    options?: QueryOptionsT
  ) => InfiniteQueryResult<InfiniteData<QueryData['response']>>;
  filterFields?: FilterField[];
  tableName: TableNameE,
  hidePagination?: boolean,
}

const InfiniteTable = <QueryData extends ListQueryRequestType<DefaultTableData>>({
  query,
  columns,
  filterFields,
  tableName,
  hidePagination,
}: Props<QueryData>) => {
  type TData = ArrayElement<QueryData['response']['data']>;
  const tableSortingMethods = useGetSortingMethods<QueryData>({ filterFields, query });
  const {
    data,
    order,
    sortBy,
    changeOrder,
    changeSortBy,
    isLoading,
    size,
  } = tableSortingMethods;
  const [columnsToRender, setColumnsToRender] = useState(columns);

  const table = useReactTable<TData>({
    data,
    columns: columnsToRender,
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
    meta: {},
  });

  return (
    <div>
      <TableContainer
        columns={columns}
        setColumnsToRender={setColumnsToRender}
        tableName={tableName}
        filterFields={filterFields}
        hidePagination={hidePagination}
        {...tableSortingMethods}
      >
        <ChakraTable
          size="sm"
          w="100%"
          // style={{ tableLayout: 'fixed' }}
          minWidth="max-content"
          width="-webkit-fill-available"
        >
          <TableHead<TData>
            table={table}
            setOrder={changeOrder}
            setSortBy={changeSortBy}
            order={order}
            sortBy={sortBy}
          />
          <TableBody<TData>
            table={table}
            data={data}
            isLoading={isLoading}
            size={size}
            draggable={false}
          />
          <TableFooter<TData> table={table} />
        </ChakraTable>
      </TableContainer>
    </div>
  );
};

export default InfiniteTable;
